/**
 * 題目型（.topic）內容的結構正規化。
 */

/**
 * 把巢狀在 #question 內的 #answer / #analyze 搬成 #question 的兄弟節點。
 *
 * 為什麼需要：部分內容檔的 .topic / #question 標籤沒有閉合。實測一份英文題的
 * 原始 HTML 是 <div> 8 個、</div> 只有 6 個，DOMParser 補完之後 #answer 就掉進
 * #question 裡面（parent 甚至是 .abreast_subject）；而數學題的標籤配對正確
 * （21 / 21），#answer 就是 .topic 的直接子元素、與 #question 平行。
 *
 * 分頁邏輯以「#answer 是不是 #question 的子孫」決定答案要不要獨立成頁
 * （見 splitForTopic108_2.js 的 answerNestedInQuestion，NBK-1666），於是同一種
 * 題型只因為內容檔少寫兩個 </div> 就走出完全不同的切法：
 *     巢狀（英文題常見）→ 答案跟題目擠在同一頁
 *     兄弟（數學題常見）→ 答案獨立一頁
 * 切法不該由 HTML 的瑕疵決定，編輯者也無從預期。這裡先正規化成兄弟結構，
 * 讓所有題目型走同一套分頁規則。
 *
 * NBK-1666 修的「答案被輸出兩次」不會因此復發：正規化後 #answer 只存在一處
 * （#question 的兄弟），question 區塊不再連帶複製它，answerNestedInQuestion
 * 也就恆為 false，那個守衛退化成防禦性檢查。
 *
 * 保持「題目 → 答案 → 解析」的文件順序（逐次推進插入點，避免兩者被顛倒）。
 *
 * @param {Document|Element} root - 已 parse 的 document 或其中一個容器元素
 * @returns {boolean} 是否實際搬動過（呼叫端可用來決定是否要重新取節點）
 */
export const normalizeAnswerSections = (root) => {
  if (!root || typeof root.querySelector !== 'function') return false;

  const questionEl = root.querySelector('#question');
  if (!questionEl || !questionEl.parentNode) return false;

  let moved = false;
  let anchor = questionEl;

  ['#answer', '#analyze'].forEach((selector) => {
    const el = questionEl.querySelector(selector);
    if (!el) return;
    questionEl.parentNode.insertBefore(el, anchor.nextSibling);
    anchor = el;
    moved = true;
  });

  return moved;
};

// 便宜的前置判斷：先用字串比對篩掉不可能需要正規化的頁，避免每頁都 parse
// （人工切頁的內容常帶大量 base64 圖片）。內容檔的引號樣式不一致，故容許
// 單雙引號與等號前後空白。
const HAS_QUESTION_ID = /id\s*=\s*['"]question['"]/;
const HAS_ANSWER_SECTION_ID = /id\s*=\s*['"](answer|analyze)['"]/;

/**
 * 對「一頁的 HTML 字串」套用 normalizeAnswerSections，回傳正規化後的字串。
 *
 * 為什麼人工切頁需要這個字串版：normalizeAnswerSections 原本只掛在
 * StaticHtmlComponent/TextStructureHandler/paging.js 的 handleTextStructure 與
 * SynopsisWindow 的 splitForTopic108_2，而那兩支都只在「程式切頁」時被呼叫。
 * 人工切頁是把編輯器匯出的 JSON 字串直接交給 setPagedHtml，完全沒經過正規化。
 *
 * 造成的問題：實測一份英文題的人工切頁 JSON，第 2 頁（答案頁）的 #answer 仍
 * 巢狀在 #question 內（內容檔標籤未閉合的後果），於是套到 NBK-1666 的覆寫
 *     .noScroll .topic #question #answer { opacity: 0 }   2 id + 2 class
 * 而不是
 *     .noScroll .topic #answer          { opacity: 1 }   1 id + 2 class
 * 整塊 opacity:0，連「答案」標題框都看不到。
 *
 * NBK-1666 那條覆寫的用意是「答案與題目同頁時不要提前顯示標題」，它以「巢狀」
 * 作為「同頁」的判斷；但該 JSON 第 2 頁的 #question 只是空殼、真正的題目在
 * 第 1 頁，巢狀在此並不代表同頁，判斷因此失效。正規化成兄弟結構即可還原成
 * 「標題可見、內容點過才展開」。
 *
 * 只有真的搬動過才重新序列化，避免對不需處理的頁做無意義的 parse/serialize。
 *
 * @param {string} pageHtml
 * @returns {string} 正規化後的 HTML；不需處理時回傳原字串（同一個參考）
 */
export const normalizeAnswerSectionsInHtml = (pageHtml) => {
  if (typeof pageHtml !== 'string') return pageHtml;
  if (!HAS_QUESTION_ID.test(pageHtml) || !HAS_ANSWER_SECTION_ID.test(pageHtml)) {
    return pageHtml;
  }
  const doc = new DOMParser().parseFromString(pageHtml, 'text/html');
  if (!normalizeAnswerSections(doc)) return pageHtml;
  return doc.body.innerHTML;
};
